// Librairies
import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, inject, OnDestroy, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { addDays, endOfDay, format, isBefore, startOfDay } from 'date-fns';
import { first, Subject, takeUntil } from 'rxjs';

// Services
import { AttachmentService } from 'src/app/services/api/attachments/attachment.service';
import { UnitService } from 'src/app/services/api/units/unit.service';

// Classes
import * as Tool from 'src/app/classes/shared/threeDTools';

// Components
import { RangePickerComponent } from 'ui-library';

@Component({
  selector: 'app-image-merging',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    RangePickerComponent,
  ],
  templateUrl: './image-merging.component.html',
  styleUrl: './image-merging.component.scss',
})
export class ImageMergingComponent implements AfterViewInit, OnDestroy {
  // Services
  private readonly attachmentService: AttachmentService = inject(AttachmentService);
  private readonly unitService: UnitService = inject(UnitService);
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly router: Router = inject(Router);

  // View
  @ViewChild('imageContainer', { static: false }) imageContainer!: ElementRef<HTMLCanvasElement>;

  // Variables
  units: any[] = [];
  attachments: any[] = [];

  startDate: string = startOfDay(new Date()).toISOString();
  endDate: string = endOfDay(new Date()).toISOString();

  canvasWidth = 2000;
  canvasHeight = 2000;
  imageScale = 1;
  posScale = 20;
  angleOffset = 0;
  crop = 0;
  minZ = 0;
  maxZ = 8849;
  focal = 200;
  angle = 45;
  loading = false;
  selectedUnit = '';

  offset = { x: 0, y: 0 };
  box: any = {
    min: { x: Infinity, y: Infinity, z: Infinity },
    max: { x: -Infinity, y: -Infinity, z: -Infinity },
    width: 0,
    height: 0,
  };

  private ctx: CanvasRenderingContext2D | null = null;
  private cvCenter = { x: 0, y: 0 };
  private mousePos: number[] = [0, 0];
  private mouseDown = false;
  private eventBinded = false;
  private readonly destroy$ = new Subject<void>();

  constructor() {
    const params = this.route.snapshot.queryParamMap;
    const startDate = params.get('startDate');
    if (startDate) this.startDate = startDate;
    const endDate = params.get('endDate');
    if (endDate) this.endDate = endDate;
    const focal = params.get('focal');
    if (focal != undefined) this.focal = parseInt(focal);
    const angle = params.get('angle');
    if (angle != undefined) this.angle = parseInt(angle);
    const crop = params.get('crop');
    if (crop != undefined) this.crop = parseInt(crop);
    const imageScale = params.get('imageScale');
    if (imageScale != undefined) this.imageScale = parseFloat(imageScale);
    const angleOffset = params.get('angleOffset');
    if (angleOffset != undefined) this.angleOffset = parseInt(angleOffset);
    const selectedUnit = params.get('selectedUnit');
    if (selectedUnit != undefined) this.selectedUnit = selectedUnit;

    this.loadUnits();
  }

  ngAfterViewInit() {
    this.bindEvents();
    this.drawCanvas(true);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadUnits() {
    this.unitService.getUnits({}).pipe(takeUntil(this.destroy$)).subscribe(res => {
      this.units = res;

      this.units.forEach((u, i) => {
        if (!this.selectedUnit && i === 0) {
          this.selectedUnit = u.id;
        }
        const pos = Tool.getXYFromLatLonAlt(u.lastPosition?.lat ?? 0, u.lastPosition?.lon ?? 0, 0);
        u.center = { x: pos[0], y: pos[1], z: u.lastPosition?.z ?? 0 };
      });

      this.getAttachments();
    });
  }

  getAttachments() {
    this.loading = true;
    this.attachmentService
      .getAttachments({ startDate: this.startDate, endDate: this.endDate, limit: 1000, unit: this.selectedUnit, populated: false, doFetch: false, track: true })
      .pipe(first())
      .subscribe(res => {
        if (!res) return;

        this.attachments = res.filter((a: any) => !!a.track && a.unit === this.selectedUnit);
        this.attachments.sort((a, b) => (a.track.z > b.track.z ? 1 : -1));
        this.attachments.forEach(att => {
          att.center = this.getCenter(att.track);
          att.center.y *= -1;
          const XY = Tool.getXYFromLatLonAlt(att.track.lat, att.track.lon, 0);

          att.position = {
            x: XY[0],
            y: XY[1] * -1,
            z: att.track.z,
          };
          att.localPos = {
            x: att.position.x - att.center.x,
            y: att.position.y - att.center.y,
            z: att.position.z - att.center.z,
          };
        });
        this.loading = false;
        this.bindEvents();
        this.drawCanvas(true);
      });
  }

  private bindEvents() {
    if (this.eventBinded || !this.imageContainer) return;
    this.eventBinded = true;
    const canvas = this.imageContainer.nativeElement;
    canvas.addEventListener('mousewheel', (evt: any) => this.zoom(evt), false);
    canvas.addEventListener('mousedown', (evt: any) => this.setMouseDown(evt), false);
    canvas.addEventListener('mouseup', (evt: any) => this.setMouseUp(evt), false);
    canvas.addEventListener('mousemove', (evt: any) => this.move(evt), false);

    this.ctx = canvas.getContext('2d');
    this.cvCenter = { x: this.canvasWidth / 2, y: this.canvasHeight / 2 };
    this.cvCenter.x += this.offset.x;
    this.cvCenter.y += this.offset.y;
  }

  navigateDate(offset: number) {
    const now = new Date();
    const newStartDate = addDays(new Date(this.startDate), offset);
    const newEndDate = addDays(new Date(this.endDate), offset);

    if (isBefore(newStartDate, now) || isBefore(newEndDate, now)) {
      this.onDateChanged({ startDate: newStartDate.toISOString(), endDate: newEndDate.toISOString() });
    }
  }

  setUnit() {
    this.setUrl();
    this.getAttachments();
  }

  onDateChanged(event: { startDate: string; endDate: string }) {
    this.startDate = new Date(event.startDate).toISOString();
    this.endDate = new Date(event.endDate).toISOString();
    this.getAttachments();
    this.setUrl();
  }

  private getCenter(track: any) {
    const hookUnit = this.units.find(u => u.trackers?.find((t: any) => t.id === track.tracker?.id || t.id === track.tracker));
    const unit = this.units.find(u => {
      if (hookUnit && (hookUnit.trackers.find((t: any) => t.id === u.crane?.hookTracker) || hookUnit.trackers.find((t: any) => t.id === u.crane?.jibTracker)))
        return true;
      if (u.crane?.hookTracker === track.tracker || u.crane?.jibTracker === track.tracker) return true;
      return false;
    });

    return unit && unit.center ? { ...unit.center } : { x: 0, y: 0, z: 0 };
  }

  private getAngle(A: any, B: any) {
    const delta_x = B.x - A.x;
    const delta_y = B.y - A.y;
    const angle_rad = Math.atan2(delta_y, delta_x);
    return angle_rad + (this.angleOffset * Math.PI) / 180;
  }

  private applyAngle(pos: { x: number; y: number }, angle: number) {
    const cosTheta = Math.cos(angle);
    const sinTheta = Math.sin(angle);
    const newX = pos.x * cosTheta - pos.y * sinTheta;
    const newY = pos.x * sinTheta + pos.y * cosTheta;
    return { x: newX, y: newY };
  }

  private drawUnits() {
    if (!this.ctx) return;
    for (let i = 1; i <= 5; i++) {
      this.ctx.beginPath();
      this.ctx.strokeStyle = '#FF0000';
      this.ctx.arc(this.cvCenter.x, this.cvCenter.y, i * this.posScale * 10, 0, 2 * Math.PI);
      this.ctx.stroke();
      this.ctx.closePath();
    }
  }

  private loadImage(url: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error(`load ${url} fail`));
      img.src = url;
    });
  }

  drawCanvas(prepare = true) {
    try {
      this.setUrl();
      if (!this.ctx) return;

      this.ctx.fillStyle = '#FFFFFF';
      this.ctx.fillRect(0, 0, this.canvasWidth, this.canvasHeight);

      this.ctx.beginPath();
      this.ctx.fillStyle = '#FFFFFF';
      this.ctx.arc(0, 0, 10, 0, 2 * Math.PI);
      this.ctx.fill();
      this.ctx.closePath();

      this.drawUnits();

      for (const att of this.attachments) {
        if (att.track.z < this.minZ || att.track.z > this.maxZ) continue;

        att.angle = this.getAngle(att.center, att.position);
        if (!att.image) {
          this.loadImage(att.url).then(img => {
            att.image = img;
            this.setImage(att);
          });
        } else {
          this.setImage(att);
        }
      }
    } catch (e) {
      console.error(e);
    }
  }

  private setImage(att: any) {
    if (!att.url || !this.ctx) return;

    const sx = (att.image.width * this.crop) / 100;
    const sy = (att.image.height * this.crop) / 100;
    const sw = att.image.width - sx * 2;
    const sh = att.image.height - sy * 2;

    att.scale = this.imageScale;
    const dw = (sw * att.scale * this.posScale) / att.image.width;
    const dh = (sh * att.scale * this.posScale) / att.image.width;
    const hw = dw / 2;
    const hh = dh / 2;
    const px = att.localPos.x * this.posScale + this.cvCenter.x;
    const py = att.localPos.y * this.posScale + this.cvCenter.y;

    const rotatedPos = this.applyAngle({ x: px, y: py }, -att.angle);
    const rx = rotatedPos.x;
    const ry = rotatedPos.y;

    this.ctx.closePath();
    this.ctx.rotate(att.angle);
    this.ctx.translate(-hw, -hh);

    this.ctx.drawImage(att.image, sx, sy, sw, sh, rx, ry, dw, dh);

    this.ctx.translate(hw, hh);
    this.ctx.rotate(-att.angle);
  }

  private zoom(e: any) {
    if (e.wheelDelta > 0) {
      this.zoomIn();
    } else {
      this.zoomOut();
    }
  }

  private redraw() {
    this.drawCanvas();
  }

  private zoomIn() {
    this.posScale += 1;
    this.redraw();
  }

  private zoomOut() {
    if (this.posScale < 1) return;
    this.posScale -= 1;
    this.redraw();
  }

  private setMouseDown(e: MouseEvent) {
    this.mouseDown = true;
    this.mousePos = [e.x, e.y];
  }

  private setMouseUp(_e: MouseEvent) {
    this.mouseDown = false;
  }

  private move(e: MouseEvent) {
    if (this.mouseDown) {
      const delta = [e.x - this.mousePos[0]!, e.y - this.mousePos[1]!];
      this.cvCenter = { x: this.cvCenter.x + delta[0]!, y: this.cvCenter.y + delta[1]! };
      this.mousePos = [e.x, e.y];
      this.redraw();
    }
  }

  private setUrl() {
    const queryParams: Params = {
      startDate: this.startDate ? new Date(this.startDate).toISOString() : undefined,
      endDate: new Date(this.endDate).toISOString(),
    };

    if (this.selectedUnit) {
      queryParams['selectedUnit'] = this.selectedUnit;
    }
    queryParams['imageScale'] = this.imageScale;
    queryParams['angleOffset'] = this.angleOffset;
    queryParams['crop'] = this.crop;
    queryParams['angle'] = this.angle;

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
    });
  }

  exportCanvas() {
    if (!this.imageContainer) return;
    const dataURL = this.imageContainer.nativeElement.toDataURL('image/jpeg');
    const link = document.createElement('a');
    link.href = dataURL;
    link.download = 'crane-view_' + format(new Date(this.startDate), 'yyyy-MM-dd') + '.jpg';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
